<template>
  <v-dialog
    v-model="alwaysOpen"
    :fullscreen="isMobile"
    width="80%"
    max-width="512px"
    @click:outside="close"
    @keydown.esc="close"
  >
    <v-card class="form-dialog form-dialog--new-user">
      <v-icon
        class="form-dialog__button form-dialog__button--close"
        @click="close"
      >
        mdi-close
      </v-icon>

      <h1 class="form-dialog__title">Date based discount - default</h1>
      <span class="form-dialog__subtitle">
        {{ subtitle() }}
      </span>
      <v-form ref="form" class="mt-8">
        <v-row dense>
          <v-col cols="12" style="height: 64px">
            <div class="text-input-label">Name of discount list</div>
            <v-text-field
              solo
              flat
              dense
              background-color="#F6F6F6"
              v-model="formData.discount_list_name"
              class="pkmn-input"
              placeholder="Enter list name"
              :disabled="isEditMode"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6" style="height: 64px">
            <div class="text-input-label">Valid from</div>
            <v-menu
              v-model="validFromMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
              :disabled="isEditMode"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatDate(formData.valid_from)"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  solo
                  flat
                  dense
                  background-color="#F6F6F6"
                  class="pkmn-input"
                  placeholder="dd.mm.yyyy"
                  append-icon="mdi-calendar"
                  :disabled="isEditMode"
                />
              </template>
              <v-date-picker
                :value="toIsoDate(formData.valid_from)"
                @input="onValidFromChange"
                no-title
                color="primary"
                :first-day-of-week="1"
              />
            </v-menu>
          </v-col>
          <v-col cols="6" style="height: 64px">
            <div
              class="text-input-label"
              :class="{ 'text-input-label--editable': isEditMode }"
            >
              Valid until
            </div>
            <v-menu
              v-model="validToMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formatDate(formData.valid_to)"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  solo
                  flat
                  dense
                  background-color="#F6F6F6"
                  class="pkmn-input"
                  :class="{ 'pkmn-input--editable': isEditMode }"
                  placeholder="dd.mm.yyyy"
                  append-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                :value="toIsoDate(formData.valid_to)"
                @input="onValidToChange"
                no-title
                color="primary"
                :first-day-of-week="1"
              />
            </v-menu>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6" style="height: 64px">
            <div class="text-input-label">Discount</div>
            <v-text-field
              solo
              flat
              dense
              background-color="#F6F6F6"
              v-model="formData.discount_percentage"
              class="pkmn-input"
              suffix="%"
              type="number"
              :min="0"
              :max="100"
              :disabled="isEditMode"
            />
          </v-col>
          <v-col cols="6" style="height: 64px">
            <div class="text-input-label">Applies to product</div>
            <v-select
              solo
              flat
              dense
              disabled
              background-color="#F6F6F6"
              :items="['Visitor parking']"
              value="Visitor parking"
              class="pkmn-input"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" style="height: 64px">
            <div class="text-input-label">Discount type</div>
            <v-select
              v-model="formData.location_ids"
              :items="locationOptions"
              item-text="text"
              item-value="id"
              multiple
              solo
              flat
              dense
              hide-details
              persistent-placeholder
              placeholder="Account default"
              background-color="#F6F6F6"
              class="pkmn-input discount-type-select"
              :menu-props="{ contentClass: 'discount-type-menu' }"
              :disabled="isEditMode"
            >
              <template v-slot:selection="{ index }">
                <span
                  v-if="index === 0 && formData.location_ids.length > 0"
                  class="discount-type-select__label"
                >
                  {{ `${formData.location_ids.length} locations` }}
                </span>
              </template>
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on" v-bind="attrs">
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="attrs.inputValue"
                      color="primary"
                      hide-details
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <data-table-holidays
          ref="holidaysTable"
          :data="formData.holidays"
          :editable="true"
          @update="onHolidaysUpdate"
        />

        <v-row class="form-dialog__button-wrap mt-4">
          <v-spacer />
          <v-col cols="auto">
            <v-btn
              class="pkmn-button pkmn-button--confirm pkmn-button--has-border save-button"
              color="primary"
              :disabled="!isFormValid"
              @click="save"
            >
              Save
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import DataTableHolidays from '../DataTable/DataTableHolidays.vue'

export default {
  name: 'FormHolidaysPopup',
  props: {
    holidays: {
      type: Array,
      default: () => []
    },
    discount: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'create',
      validator: (val) => ['create', 'edit'].includes(val)
    },
    isLocationProduct: {
      type: Boolean,
      default: false
    },
    locations: {
      type: Array,
      default: () => []
    },
    selectedLocationIds: {
      type: Array,
      default: () => []
    }
  },
  components: {
    DataTableHolidays
  },
  data() {
    return {
      alwaysOpen: true,
      mobileBreakpoint: 767,
      validFromMenu: false,
      validToMenu: false,
      formData: {
        discount_list_name: '',
        valid_from: null,
        valid_to: null,
        discount_percentage: null,
        holidays: [],
        location_ids: []
      },
      updatedHolidays: []
    }
  },
  computed: {
    isMobile() {
      return window.innerWidth <= this.mobileBreakpoint
    },
    isEditMode() {
      return this.mode === 'edit'
    },
    locationOptions() {
      return (this.locations || []).map((loc) => ({
        id: loc.id,
        text: `${loc.name || `Location ${loc.id}`} - ID: ${loc.id}`
      }))
    },
    isFormValid() {
      if (!this.formData.discount_list_name) return false
      if (!this.formData.valid_from) return false
      if (
        this.formData.discount_percentage === null ||
        this.formData.discount_percentage === '' ||
        this.formData.discount_percentage < 0 ||
        this.formData.discount_percentage > 100
      ) {
        return false
      }
      if (this.updatedHolidays.length === 0) return false
      const dates = this.updatedHolidays.map((h) => h.holiday_date)
      if (dates.some((d) => !d)) return false
      return new Set(dates).size === dates.length
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    formatDate(timestamp) {
      if (!timestamp) return ''
      return moment.unix(timestamp).format('DD.MM.YYYY')
    },
    toIsoDate(timestamp) {
      if (!timestamp) return null
      return moment.unix(timestamp).format('YYYY-MM-DD')
    },
    onValidFromChange(isoDate) {
      this.formData.valid_from = moment(isoDate, 'YYYY-MM-DD')
        .startOf('day')
        .unix()
      this.validFromMenu = false
    },
    onValidToChange(isoDate) {
      this.formData.valid_to = moment(isoDate, 'YYYY-MM-DD')
        .startOf('day')
        .unix()
      this.validToMenu = false
    },
    onHolidaysUpdate(items) {
      this.updatedHolidays = items
    },
    save() {
      const table = this.$refs.holidaysTable
      if (!table || !table.isValid()) return

      // Backend rejects valid_from older than (server now − 3s). When the user
      // picks today, midnight is hours in the past, so swap in "now" at the
      // moment Save is clicked.
      const now = moment().unix()
      const validFrom =
        this.formData.valid_from && this.formData.valid_from < now
          ? now
          : this.formData.valid_from

      const payload = {
        discount_list_name: this.formData.discount_list_name,
        discount_type: 'holiday_discount',
        product_name: 'visitor_parking',
        valid_from: validFrom,
        valid_to: this.formData.valid_to,
        discount_percentage: Number(this.formData.discount_percentage),
        holidays: this.updatedHolidays.map((h) => ({
          holiday_date_id: h.holiday_date_id ?? null,
          holiday_date: h.holiday_date ?? null
        })),
        location_ids: this.formData.location_ids
      }

      if (this.mode === 'edit' && this.discount) {
        payload.list_id =
          this.discount.list_id || this.discount.discount_list_id
      }

      this.$emit('save', payload, this.mode)
    },
    subtitle() {
      if (this.isLocationProduct) {
        return 'This is your account default holidays. All your locations will by default have these discounts. To change the discount settings for this location only,  please contact parkman customer support'
      } else {
        return 'This is your account default holidays. All your locations will by default have these discounts'
      }
    }
  },
  created() {
    if (this.mode === 'edit' && this.discount) {
      this.formData.discount_list_name =
        this.discount.discount_list_name || ''
      this.formData.valid_from = this.discount.valid_from || null
      this.formData.valid_to = this.discount.valid_to || null
      this.formData.discount_percentage =
        this.discount.discount_percentage ?? null
      this.formData.holidays = (this.holidays || []).map((h) => ({
        holiday_date_id: h.holiday_date_id ?? null,
        holiday_date: h.holiday_date ?? null
      }))
    } else {
      this.formData.discount_list_name = ''
      this.formData.valid_from = null
      this.formData.valid_to = null
      this.formData.discount_percentage = null
      this.formData.holidays = []
    }
    this.formData.location_ids = [...(this.selectedLocationIds || [])]
    this.updatedHolidays = this.formData.holidays
  }
}
</script>

<style lang="scss">
@import '@/style/common';

.v-text-field__details {
  padding: 0px !important;
}

.col:has(.v-input--is-focused) {
  .text-input-label {
    color: $color-brand;
  }
}

.form-dialog--new-user .v-input--is-focused .v-input__slot {
  border: 1px solid $color-brand !important;
}

.form-dialog--new-user .v-input.error--text .v-input__slot {
  border: 1px solid $color-danger !important;
}

// In edit mode, the only editable field is Valid until — give it a steady
// green border + label color so the user sees what they can change.
.pkmn-input--editable .v-input__slot {
  border: 1px solid $color-brand !important;
}

.text-input-label--editable {
  color: $color-brand;
}

.save-button {
  height: 31px !important;
}

.discount-type-menu .v-list-item {
  min-height: 34px !important;
  height: 34px !important;
  padding: 0 12px !important;
}

.discount-type-menu .v-list-item__action {
  margin: 0 8px 0 0 !important;
  align-self: center !important;
}

.discount-type-menu .v-list-item__content {
  padding: 0 !important;
  align-self: center !important;
}

.discount-type-menu .v-list-item__title {
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
}

.discount-type-menu .v-list-item--active {
  background-color: transparent !important;
  color: inherit !important;
}

.discount-type-menu .v-list-item--active::before {
  opacity: 0 !important;
}

.discount-type-menu .v-list-item--active .v-list-item__title {
  color: inherit !important;
}
</style>
